import React, { useState } from 'react';
import api from '../services/api';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ListFooter } from '../ui';

// Sales Plan pieces embedded in the Merchandising page:
// row button, create modal (price → revenue + margin vs CE), plans tab w/ chart.

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const inr = (v) => '₹' + num(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const inputStyle = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box', width: '100%' };
const labelStyle = { fontSize: 9, color: '#888', fontWeight: 600, display: 'block', marginBottom: 2, textTransform: 'uppercase' };
const btnPrimary = { padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };

export function SalesPlanButton({ order, plan, onClick }) {
  return plan ? (
    <button onClick={onClick} title={`Plan ${plan.plan_no} · ${inr(plan.planned_revenue_inr)} planned`}
      style={{ padding: '3px 10px', background: '#EAF7EE', color: '#166534', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap' }}>
      📈 {inr(plan.planned_revenue_inr)}</button>
  ) : (
    <button onClick={onClick} title="Add sales plan"
      style={{ padding: '3px 10px', background: '#f0f2f8', color: '#666', border: '1px solid #ddd', borderRadius: 4, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
      📈 Add Plan</button>
  );
}

export function SalesPlanModal({ order, plan, costPerPc, user, onClose, onSaved }) {
  const [price, setPrice] = useState(plan?.selling_price_per_pc || order.fob_price || '');
  const [shipDate, setShipDate] = useState(plan?.planned_shipment_date ? String(plan.planned_shipment_date).slice(0, 10) : (order.delivery_date ? String(order.delivery_date).slice(0, 10) : ''));
  const [remarks, setRemarks] = useState(plan?.remarks || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const qty = num(order.quantity || order.order_qty);
  const revenue = qty * num(price);
  const margin = num(price) > 0 && costPerPc > 0 ? ((num(price) - costPerPc) / num(price)) * 100 : null;
  const save = async () => {
    setBusy(true); setError('');
    const body = {
      gpo_no: order.gpo_no || order.order_no, buyer: order.buyer_name || order.brand,
      style_no: order.style_no, garment_desc: order.garment_desc || order.item_desc,
      order_qty: qty, planned_shipment_date: shipDate || undefined,
      selling_price_per_pc: price, remarks,
    };
    try {
      if (plan) await api.patch('/finance/sales-plan/' + plan.id, { selling_price_per_pc: price, planned_shipment_date: shipDate || undefined, remarks });
      else await api.post('/finance/sales-plan', body);
      onSaved();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 460, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>📈 Sales Plan — {order.gpo_no || order.order_no}</h3></div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#888' }}>
          {order.buyer_name || order.brand || '—'} · {order.style_no || '—'} · {qty.toLocaleString('en-IN')} pcs · delivery {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-IN') : '—'} · merch {user?.name}</p>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>Selling Price / pc ₹ *</label>
            <input type="number" step="0.01" style={inputStyle} value={price} onChange={e => setPrice(e.target.value)} autoFocus /></div>
          <div><label style={labelStyle}>Planned Shipment Date</label>
            <input type="date" style={inputStyle} value={shipDate} onChange={e => setShipDate(e.target.value)} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Remarks</label>
            <input style={inputStyle} value={remarks} onChange={e => setRemarks(e.target.value)} /></div>
        </div>
        <div style={{ marginTop: 12, background: '#F7F6F3', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div><span style={{ fontSize: 10, color: '#888' }}>PLANNED REVENUE </span><strong style={{ fontSize: 17, color: '#3B3A8F' }}>{inr(revenue)}</strong></div>
          <div><span style={{ fontSize: 10, color: '#888' }}>COST/PC (CE) </span><strong>{costPerPc > 0 ? inr(costPerPc) : '—'}</strong></div>
          <div><span style={{ fontSize: 10, color: '#888' }}>GROSS MARGIN </span>
            <strong style={{ fontSize: 16, color: margin === null ? '#888' : margin > 15 ? '#166534' : '#A32D2D' }}>
              {margin === null ? '—' : margin.toFixed(1) + '%'}</strong></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy || !num(price)} onClick={save} style={btnPrimary}>{busy ? '…' : plan ? '💾 Update Plan' : '💾 Save Plan'}</button>
        </div>
      </div>
    </div>
  );
}

export function SalesPlanTab({ plans, onChanged }) {
  const [actualsFor, setActualsFor] = useState(null);
  const totals = plans.reduce((t, p) => ({
    planned: t.planned + num(p.planned_revenue_inr),
    actual: t.actual + num(p.actual_revenue_inr),
  }), { planned: 0, actual: 0 });
  // Monthly planned vs actual for the chart.
  const byMonth = {};
  plans.forEach(p => {
    const m = p.plan_month || '—';
    if (!byMonth[m]) byMonth[m] = { month: m, planned: 0, actual: 0 };
    byMonth[m].planned += num(p.planned_revenue_inr); byMonth[m].actual += num(p.actual_revenue_inr);
  });
  const chart = Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month));
  const thS = { padding: '8px 9px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const tdS = { padding: '8px 9px' };
  return (
    <div>
      {chart.length > 0 && (
        <div style={{ marginBottom: 16, background: 'white', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', marginBottom: 6 }}>Planned vs Actual Revenue by Month</div>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} tickFormatter={v => '₹' + (v / 100000).toFixed(1) + 'L'} />
              <Tooltip formatter={v => inr(v)} /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="planned" name="Planned" fill="#3B3A8F" radius={[4, 4, 0, 0]} barSize={26} />
              <Bar dataKey="actual" name="Actual" fill="#16a34a" radius={[4, 4, 0, 0]} barSize={26} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {['GPO No', 'Buyer', 'Style', 'Month', 'Order Qty', 'Selling Price', 'Planned Revenue', 'Actual Qty', 'Actual Revenue', 'Variance', 'Status', ''].map(h => <th key={h} style={thS}>{h}</th>)}
        </tr></thead>
        <tbody>
          {plans.length === 0 ? <tr><td colSpan={12} style={{ padding: 22, textAlign: 'center', color: '#aaa' }}>No sales plans yet — use the 📈 button on an order row.</td></tr>
            : plans.map(p => {
              const variance = num(p.actual_revenue_inr) - num(p.planned_revenue_inr);
              return (
                <tr key={p.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ ...tdS, fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{p.gpo_no}</td>
                  <td style={tdS}>{p.buyer || '—'}</td><td style={tdS}>{p.style_no || '—'}</td><td style={tdS}>{p.plan_month}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{num(p.order_qty).toLocaleString('en-IN')}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{inr(p.selling_price_per_pc)}</td>
                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{inr(p.planned_revenue_inr)}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{p.actual_qty_shipped ?? '—'}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{p.actual_revenue_inr ? inr(p.actual_revenue_inr) : '—'}</td>
                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: variance >= 0 ? '#166534' : '#A32D2D' }}>{p.actual_revenue_inr ? inr(variance) : '—'}</td>
                  <td style={tdS}>{p.status}</td>
                  <td style={tdS}><button onClick={() => setActualsFor(p)} style={{ ...btnPrimary, padding: '3px 10px', fontSize: 10, background: '#EEEDF8', color: '#3B3A8F' }}>✎ Update Actuals</button></td>
                </tr>
              );
            })}
        </tbody>
        {plans.length > 0 && (
          <tfoot><tr style={{ background: '#f4f5fa', fontWeight: 800 }}>
            <td colSpan={6} style={tdS}>TOTAL</td>
            <td style={{ ...tdS, textAlign: 'right' }}>{inr(totals.planned)}</td>
            <td style={tdS}></td>
            <td style={{ ...tdS, textAlign: 'right' }}>{inr(totals.actual)}</td>
            <td style={{ ...tdS, textAlign: 'right', color: totals.actual - totals.planned >= 0 ? '#166534' : '#A32D2D' }}>{inr(totals.actual - totals.planned)}</td>
            <td colSpan={2} style={tdS}></td>
          </tr></tfoot>
        )}
      </table>
      <ListFooter shown={plans.length} total={plans.length} noun="sales plans" />
      {actualsFor && <ActualsModal plan={actualsFor} onClose={() => setActualsFor(null)} onSaved={() => { setActualsFor(null); onChanged(); }} />}
    </div>
  );
}

function ActualsModal({ plan, onClose, onSaved }) {
  const [form, setForm] = useState({
    actual_shipment_date: plan.actual_shipment_date ? String(plan.actual_shipment_date).slice(0, 10) : '',
    actual_qty_shipped: plan.actual_qty_shipped ?? plan.order_qty,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const actualRev = num(form.actual_qty_shipped) * num(plan.selling_price_per_pc);
  const save = async () => {
    setBusy(true); setError('');
    try { await api.patch('/finance/sales-plan/' + plan.id, form); onSaved(); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 380, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>✎ Update Actuals — {plan.gpo_no}</h3></div>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div style={{ display: 'grid', gap: 10 }}>
          <div><label style={labelStyle}>Actual Shipment Date</label>
            <input type="date" style={inputStyle} value={form.actual_shipment_date} onChange={e => setForm({ ...form, actual_shipment_date: e.target.value })} /></div>
          <div><label style={labelStyle}>Actual Qty Shipped</label>
            <input type="number" style={inputStyle} value={form.actual_qty_shipped ?? ''} onChange={e => setForm({ ...form, actual_qty_shipped: e.target.value })} /></div>
          <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
            Actual Revenue (auto): <strong style={{ color: '#3B3A8F', fontSize: 14 }}>{inr(actualRev)}</strong></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={save} style={{ ...btnPrimary, background: '#166534' }}>{busy ? '…' : '💾 Save Actuals'}</button>
        </div>
      </div>
    </div>
  );
}
